// Implements: F-UIX-018
/**
 * The audit list's filters (UIX.7): a range of two days (either may stay empty for the server's
 * default, the last 366 days), an action ("all revocations" covers both revocation tokens), a team
 * and a mockup. Pure presentation: the draft is the container's, applied with "Aplicar"; a range
 * the container refuses is announced under the form.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { AUDIT_ACTIONS } from "../../../types/audit";
import type { ActionFilter, AuditFilters } from "../../../types/audit";
import styles from "./Audit.module.css";
import { ACTION_LABEL_KEYS } from "./auditActionLabels";

/** A team or mockup the filter offers, by id and name. */
export interface FilterOption {
  readonly id: string;
  readonly name: string;
}

export interface AuditFiltersFormProps {
  readonly draft: AuditFilters;
  readonly teams: readonly FilterOption[];
  readonly mockups: readonly FilterOption[];
  /** The sentence of a range the container refused, or `null`. */
  readonly problem: string | null;
  readonly onChange: (draft: AuditFilters) => void;
  readonly onApply: () => void;
  readonly onClear: () => void;
}

export function AuditFiltersForm({
  draft,
  teams,
  mockups,
  problem,
  onChange,
  onApply,
  onClear,
}: AuditFiltersFormProps): ReactElement {
  const { t } = useTranslation();
  const ids = useId();

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onApply();
      }}
    >
      <fieldset className={styles.filters} aria-describedby={`${ids}-hint`}>
        <legend className={styles.legend}>{t("backoffice.audit.filters.legend")}</legend>
        <div className={styles.field}>
          <label htmlFor={`${ids}-from`}>{t("backoffice.audit.filters.from")}</label>
          <input
            id={`${ids}-from`}
            type="date"
            value={draft.from}
            onChange={(event) => {
              onChange({ ...draft, from: event.target.value });
            }}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor={`${ids}-to`}>{t("backoffice.audit.filters.to")}</label>
          <input
            id={`${ids}-to`}
            type="date"
            value={draft.to}
            onChange={(event) => {
              onChange({ ...draft, to: event.target.value });
            }}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor={`${ids}-action`}>{t("backoffice.audit.filters.action")}</label>
          <select
            id={`${ids}-action`}
            value={draft.action}
            onChange={(event) => {
              onChange({ ...draft, action: event.target.value as ActionFilter });
            }}
          >
            <option value="">{t("backoffice.audit.filters.allActions")}</option>
            <option value="all_revocations">{t("backoffice.audit.filters.allRevocations")}</option>
            {AUDIT_ACTIONS.map((action) => (
              <option key={action} value={action}>
                {t(ACTION_LABEL_KEYS[action])}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label htmlFor={`${ids}-team`}>{t("backoffice.audit.filters.team")}</label>
          <select
            id={`${ids}-team`}
            value={draft.teamId}
            onChange={(event) => {
              onChange({ ...draft, teamId: event.target.value });
            }}
          >
            <option value="">{t("backoffice.audit.filters.allTeams")}</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label htmlFor={`${ids}-mockup`}>{t("backoffice.audit.filters.mockup")}</label>
          <select
            id={`${ids}-mockup`}
            value={draft.mockupId}
            onChange={(event) => {
              onChange({ ...draft, mockupId: event.target.value });
            }}
          >
            <option value="">{t("backoffice.audit.filters.allMockups")}</option>
            {mockups.map((mockup) => (
              <option key={mockup.id} value={mockup.id}>
                {mockup.name}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" type="submit">
            {t("backoffice.audit.filters.apply")}
          </Button>
          <Button variant="ghost" type="button" onClick={onClear}>
            {t("backoffice.audit.filters.clear")}
          </Button>
        </div>
        <p id={`${ids}-hint`} className={styles.hint}>
          {t("backoffice.audit.filters.rangeHint")}
        </p>
      </fieldset>
      {problem === null ? null : (
        <p role="alert" className={styles.problem}>
          {problem}
        </p>
      )}
    </form>
  );
}
